import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { teamAPI, heartbeatAPI, capsulesAPI, tenantsAPI, capsuleConfigAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Users, HeartPulse, Plus, Trash2, Save, Loader2, Bell, Clock, ChevronDown, ChevronUp, ArrowUpDown, Wifi, WifiOff, UserPlus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import OrgProfileAttributes, { orgProfileSummary } from '@/components/OrgProfileAttributes';

const CHANNEL_TYPES = [
  { value: 'webchat', label: 'WebChat (interno)' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'email', label: 'Email' },
];

function isValidContactAddress(channelType, address) {
  const ct = channelType || '';
  const addr = (address || '').trim();
  if (ct === 'webchat') return true;
  if (ct === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr);
  if (ct === 'whatsapp') {
    const digits = addr.replace(/\D/g, '');
    return digits.length >= 8 && digits.length <= 15;
  }
  if (ct === 'teams') return Boolean(addr) && !/\s/.test(addr);
  return false;
}

function TeamMemberCard({ member, onUpdate, onDelete, onUpdateCredentials, onSyncOrg, onRecargar, isAdmin, capsules, rolesPorCapsula = {} }) {
  const [expanded, setExpanded] = useState(false);
  const [syncingOrgOne, setSyncingOrgOne] = useState(false);
  const [confirmarReset, setConfirmarReset] = useState(false);
  const resetearSegundoFactor = async () => {
    try {
      await teamAPI.resetearSegundoFactor(member.user_email);
      toast.success('Doble factor reseteado: lo va a configurar de nuevo en su próximo ingreso');
      onRecargar?.();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo resetear el doble factor');
    }
  };
  const [expertise, setExpertise] = useState(member.expertise?.join(', ') || '');
  const [channels, setChannels] = useState(member.channels || []);
  const [capsuleIds, setCapsuleIds] = useState(member.capsule_ids || []);
  // `[{capsule_id, role_id}]` y no `{capsula: [roles]}`: Mongo prohibe `.` y `$` en las
  // claves de un documento, y el capsule_id lo escribe el usuario.
  const [roles, setRoles] = useState(member.roles || []);
  const [notificationsEnabled, setNotificationsEnabled] = useState(member.notifications_enabled !== false);
  const [status, setStatus] = useState(member.status || 'active');
  const [saving, setSaving] = useState(false);
  const [testingIdx, setTestingIdx] = useState(null);

  // Credential editing
  const [editFullName, setEditFullName] = useState(member.full_name || '');
  const [editUsername, setEditUsername] = useState(member.username || '');
  const [editRole, setEditRole] = useState(member.role || 'operator');
  // **Qué tocó una persona de verdad, y no qué quedó distinto.**
  //
  // El 2026-08-28 Diego vio que al abrir cualquier miembro del equipo aparecía
  // `admin@autochat.com.ar` con su contraseña. Era el navegador: un campo de texto seguido de
  // uno `type="password"` y sin `autoComplete`, así que el gestor de contraseñas rellenaba las
  // credenciales del sitio.
  //
  // Lo grave no era verlo. `handleSaveCredentials` mandaba lo que **estuviera distinto** de
  // `member.*`, y para alguien importado de M365 `member.username` está vacío: lo autocompletado
  // era distinto, así que se mandaba. Guardar sin mirar le escribía el usuario y la contraseña
  // del admin a otra persona.
  //
  // Los atributos de abajo le piden al navegador que no lo haga; esto **garantiza** que no pase
  // aunque los ignore, que es lo que hacen a veces. El navegador puede rellenar un campo; no
  // puede hacer que alguien lo escriba.
  const [tocoCredenciales, setTocoCredenciales] = useState({});
  const marcarTocado = (campo) => setTocoCredenciales((prev) => ({ ...prev, [campo]: true }));
  const [newPassword, setNewPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);

  const addChannel = () => {
    setChannels(prev => [...prev, {
      channel_type: 'email',
      address: member.user_email || '',
      priority: prev.length + 1,
      enabled: true,
    }]);
  };

  const updateChannel = (idx, field, value) => {
    setChannels(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const next = { ...c, [field]: value };
      if (field === 'channel_type') {
        if (value === 'webchat') next.address = 'internal';
        else if (value === 'email' && (!c.address || c.address === 'internal')) {
          next.address = member.user_email || '';
        }
      }
      return next;
    }));
  };

  const removeChannel = (idx) => setChannels(prev => prev.filter((_, i) => i !== idx));

  const handleTestChannel = async (idx) => {
    const ch = channels[idx];
    if (!ch) return;
    if (!isValidContactAddress(ch.channel_type, ch.address)) {
      toast.error('Contacto invalido: corrige el formato antes de probar');
      return;
    }
    setTestingIdx(idx);
    try {
      const res = await teamAPI.testChannel(member.user_email, {
        channel_type: ch.channel_type,
        address: ch.address,
        capsule_id: capsuleIds[0] || undefined,
      });
      const data = res.data || {};
      if (data.sent) {
        toast.success(`Prueba OK. Emisor: ${data.sender || 'n/d'}`);
      } else {
        toast.error(`No enviado: ${data.reason || 'error'} (emisor: ${data.sender || 'ninguno'})`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al probar contacto');
    } finally {
      setTestingIdx(null);
    }
  };

  const [guardandoAvisos, setGuardandoAvisos] = useState(false);

  const cambiarAvisos = async (valor) => {
    setNotificationsEnabled(valor);
    setGuardandoAvisos(true);
    try {
      await onUpdate(member.user_email, { notifications_enabled: valor });
      toast.success(valor
        ? 'Vuelve a recibir avisos por sus canales'
        : 'Deja de recibir avisos: los ve solo en la aplicacion');
    } catch (err) {
      // **Se vuelve atras en la pantalla.** Un switch que queda apagado sobre un guardado que
      // fallo le hace creer a alguien que esa persona dejo de recibir avisos, y no es cierto.
      setNotificationsEnabled(!valor);
      toast.error(formatApiError(err, 'No se pudo cambiar'));
    } finally {
      setGuardandoAvisos(false);
    }
  };

  const handleSave = async () => {
    for (const ch of channels) {
      if (ch.enabled !== false && !isValidContactAddress(ch.channel_type, ch.address)) {
        toast.error(`Contacto invalido (${ch.channel_type}): ${ch.address || '(vacio)'}`);
        return;
      }
    }
    setSaving(true);
    try {
      await onUpdate(member.user_email, {
        capsule_ids: capsuleIds,
        roles,
        expertise: expertise.split(',').map(s => s.trim()).filter(Boolean),
        channels,
        notifications_enabled: notificationsEnabled,
        status,
      });
      toast.success('Miembro actualizado');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
    }
    finally { setSaving(false); }
  };

  const handleSaveCredentials = async () => {
    setSavingCreds(true);
    try {
      const payload = {};
      if (tocoCredenciales.full_name && editFullName !== member.full_name) payload.full_name = editFullName;
      if (tocoCredenciales.username && editUsername !== member.username) payload.username = editUsername;
      if (editRole !== member.role) payload.role = editRole;
      if (tocoCredenciales.password && newPassword) payload.password = newPassword;
      if (Object.keys(payload).length === 0) {
        toast.info('No hay cambios para guardar');
        setSavingCreds(false);
        return;
      }
      await onUpdateCredentials(member.user_email, payload);
      setNewPassword('');
      toast.success('Credenciales actualizadas');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar credenciales'); }
    finally { setSavingCreds(false); }
  };

  const statusColors = { active: 'bg-green-500', vacation: 'bg-amber-400', unavailable: 'bg-zinc-300' };

  return (
    <>
    <ConfirmActionDialog
      open={confirmarReset}
      onOpenChange={setConfirmarReset}
      title="Resetear el doble factor"
      description={`${member.full_name || member.user_email} no va a poder entrar con su autenticador actual. En su próximo ingreso va a tener que escanear un código QR nuevo. Usalo cuando perdió o cambió el teléfono.`}
      confirmText="Resetear"
      onConfirm={async () => { setConfirmarReset(false); await resetearSegundoFactor(); }}
    />
    <Card className={`border rounded-sm transition-all ${status === 'active' ? 'border-brand-border' : 'border-zinc-200 opacity-70'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${statusColors[status] || 'bg-zinc-300'}`} />
            <div>
              <span className="text-sm font-medium text-[#09090B]">{member.full_name || member.user_email}</span>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge variant="outline" className="text-[9px] rounded-sm">{member.role}</Badge>
                {member.auth_provider && member.auth_provider !== 'local' && (
                  <Badge className="text-[9px] rounded-sm bg-sky-100 text-sky-800 border-0">Microsoft</Badge>
                )}
                {member.user_status === 'pending_approval' && (
                  <Badge className="text-[9px] rounded-sm bg-amber-100 text-amber-800 border-0">Pendiente</Badge>
                )}
                {/* El segundo factor es obligatorio en toda cuenta con contraseña local (2026-10-13). */}
                {member.usa_contrasena && (
                  member.segundo_factor_activo
                    ? <Badge className="text-[9px] rounded-sm bg-emerald-100 text-emerald-800 border-0" data-testid={`2fa-activo-${member.user_email}`}>2FA activo</Badge>
                    : <Badge className="text-[9px] rounded-sm bg-amber-100 text-amber-800 border-0" data-testid={`2fa-pendiente-${member.user_email}`} title="Lo va a configurar en su próximo ingreso">2FA sin configurar</Badge>
                )}
                <span className="text-[10px] text-zinc-400">{member.username}</span>
                <span className="text-[10px] text-zinc-300">|</span>
                <span className="text-[10px] text-zinc-400">{member.user_email}</span>
              </div>
              {(member.org_profile?.job_title || member.org_profile?.department || member.org_profile?.manager_display_name) && (
                <p className="text-[10px] text-zinc-600 mt-1">
                  {orgProfileSummary(member.org_profile)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Por dónde se le puede avisar de verdad: la agenda cargada a mano, o sus
                identidades si no hay agenda. Contar solo la agenda mostraba "0 canales" a
                alguien con correo y teléfono cargados — y desde que los avisos caen a las
                identidades, ese cero decía algo falso. */}
            <Badge variant="secondary" className="text-[9px] rounded-sm">
              {(member.vias_de_contacto || []).length || channels.length} vías de aviso
            </Badge>
            {/* Rotulado desde el 2026-08-22. Era un interruptor pelado al lado del badge, y
                Diego no lo encontraba — con razon: nada decia que hacia. Y hasta ese dia
                tampoco apagaba nada: la preferencia estaba en la ficha y el aviso de tarea ni
                la miraba. */}
            <div className="flex items-center gap-1.5"
                 title="Si se apaga, esta persona no recibe avisos por correo, Teams ni WhatsApp: los ve en la aplicacion, en Mi trabajo.">
              <Label className="text-[10px] text-zinc-500 cursor-pointer"
                     htmlFor={`notify-${member.user_email}`}>
                {notificationsEnabled ? 'Avisar por sus canales' : 'Solo en la app'}
              </Label>
              {/* **Guarda al tocarlo.** Antes solo cambiaba el estado local y el boton
                  "Guardar" vive dentro del panel desplegado: con la ficha cerrada, tocar el
                  switch no mandaba nada y al recargar volvia como estaba. Y el resto del
                  producto —memorias, casos resueltos, avisos— guarda los switches al tocarlos,
                  asi que este parecia uno de esos y no lo era.
                  Es ademas un interruptor que le corta los avisos a una persona real: que
                  quede en "apagado" en la pantalla sin estarlo es la peor de las dos mentiras
                  posibles. */}
              <Switch id={`notify-${member.user_email}`}
                data-testid={`notify-toggle-${member.user_email}`}
                checked={notificationsEnabled}
                disabled={guardandoAvisos}
                onCheckedChange={cambiarAvisos} />
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-7 w-7 p-0">
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        {/* Expertise badges */}
        {(member.expertise || []).length > 0 && !expanded && (
          <div className="flex flex-wrap gap-1 mt-2 ml-6">
            {member.expertise.map((e, i) => (
              <Badge key={i} variant="secondary" className="text-[9px] rounded-sm bg-blue-50 text-blue-700">{e}</Badge>
            ))}
          </div>
        )}
        {(member.capsule_ids || []).length > 0 && !expanded && (
          <div className="flex flex-wrap gap-1 mt-1 ml-6">
            {(member.capsule_ids || []).map((cid, i) => (
              <Badge key={`${cid}-${i}`} variant="outline" className="text-[9px] rounded-sm">{cid}</Badge>
            ))}
          </div>
        )}

        {/* Expanded editor */}
        {expanded && (
          <div className="mt-4 space-y-4 border-t border-brand-border pt-3" data-testid={`team-edit-${member.user_email}`}>

            {/* Credentials section (admin only) */}
            {isAdmin && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 space-y-2">
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Datos de Cuenta</Label>
                {/* **Cómo entra esta persona, dicho antes de mostrar un formulario de usuario y
                    contraseña.** Un formulario en blanco no distingue tres situaciones muy
                    distintas: quien tiene clave local, quien entra con Microsoft, y quien
                    directamente no tiene acceso a la consola — que es el caso de casi todos los
                    importados del directorio, y es lo que hacía que el formulario se leyera como
                    "acá va tu usuario". */}
                {!member.tiene_login ? (
                  <p className="text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-sm p-2"
                     data-testid={`cred-sin-acceso-${member.user_email}`}>
                    <b>No tiene acceso a la consola.</b> Está en el equipo —se le pueden asignar
                    casos y avisos— pero no entra a OmniSyS. Si querés darle acceso, completá
                    usuario y contraseña acá abajo; si va a entrar con su cuenta de Microsoft, no
                    hace falta nada.
                  </p>
                ) : member.auth_provider && member.auth_provider !== 'local' ? (
                  <p className="text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-sm p-2"
                     data-testid={`cred-entra-por-microsoft-${member.user_email}`}>
                    <b>Entra con su cuenta de Microsoft.</b> La contraseña la maneja la
                    organización, no OmniSyS: dejá el campo vacío. Poner una acá no le cambia la
                    forma de entrar.
                  </p>
                ) : (
                  <p className="text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-sm p-2"
                     data-testid={`cred-local-${member.user_email}`}>
                    <b>Entra con usuario y contraseña de OmniSyS.</b>
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Nombre Completo</Label>
                    <Input data-testid={`cred-fullname-${member.user_email}`} value={editFullName}
                      onChange={e => { setEditFullName(e.target.value); marcarTocado('full_name'); }}
                      autoComplete="off" name={`nombre-${member.user_email}`}
                      className="rounded-sm text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Usuario</Label>
                    {/* `username` a secas es lo que los navegadores buscan para autocompletar.
                        Un `name` propio de esta fila y `autoComplete="off"` los desorientan. */}
                    <Input data-testid={`cred-username-${member.user_email}`} value={editUsername}
                      onChange={e => { setEditUsername(e.target.value); marcarTocado('username'); }}
                      autoComplete="off" name={`usuario-de-${member.user_email}`}
                      className="rounded-sm text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Rol</Label>
                    <select data-testid={`cred-role-${member.user_email}`} value={editRole}
                      onChange={e => setEditRole(e.target.value)}
                      className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8">
                      {/* `solicitante` faltaba: el rol existe desde el 2026-08-18 y no se
                          podia ni poner ni sacar desde acá, así que alguien dado de alta
                          automáticamente quedaba atrapado en el rol más acotado. */}
                      <option value="solicitante">Solicitante (solo ve lo que pidió)</option>
                      <option value="operator">Operador</option>
                      <option value="supervisor">Supervisor</option>
                      {/* Dirección ordena la prioridad de la empresa entre todas las gerencias
                          (2026-10-09). No estaba en este selector, así que no se podía dar. */}
                      <option value="executive">Dirección (ordena la prioridad de la empresa)</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Nueva Contrasena (dejar vacio para no cambiar)</Label>
                  <div className="relative">
                    <Input data-testid={`cred-password-${member.user_email}`}
                      type={showPwd ? 'text' : 'password'} value={newPassword}
                      onChange={e => { setNewPassword(e.target.value); marcarTocado('password'); }}
                      autoComplete="new-password" name={`clave-de-${member.user_email}`}
                      placeholder="Sin cambios" className="rounded-sm text-xs h-8 pr-9" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                      tabIndex={-1}>
                      {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  {member.usa_contrasena && member.segundo_factor_activo && (
                    <Button size="sm" variant="outline" onClick={() => setConfirmarReset(true)}
                      data-testid={`resetear-2fa-${member.user_email}`}
                      className="rounded-sm text-xs h-7 px-3">
                      Resetear doble factor
                    </Button>
                  )}
                  <Button size="sm" onClick={handleSaveCredentials} disabled={savingCreds}
                    data-testid={`save-creds-${member.user_email}`}
                    className="bg-zinc-800 text-white rounded-sm text-xs h-7 px-3">
                    {savingCreds ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar Cuenta
                  </Button>
                </div>
              </div>
            )}

            <OrgProfileAttributes
              orgProfile={member.org_profile}
              refreshing={syncingOrgOne}
              onRefresh={onSyncOrg ? async () => {
                setSyncingOrgOne(true);
                try {
                  await onSyncOrg(member.user_email);
                } finally {
                  setSyncingOrgOne(false);
                }
              } : undefined}
            />

            {/* Team config section */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Capsulas asignadas</Label>
                <div className="border border-brand-border rounded-sm p-2 max-h-28 overflow-auto space-y-1 bg-white">
                  {(capsules || []).map((c) => {
                    const checked = capsuleIds.includes(c.capsule_id);
                    return (
                      <label key={c.capsule_id} className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) setCapsuleIds((prev) => [...new Set([...prev, c.capsule_id])]);
                            else setCapsuleIds((prev) => {
                              const next = prev.filter((id) => id !== c.capsule_id);
                              return next.length ? next : [];
                            });
                          }}
                        />
                        <span>{c.capsule_id}</span>
                      </label>
                    );
                  })}
                  {!capsules?.length && <p className="text-[10px] text-zinc-400">Sin capsulas disponibles</p>}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Rol en cada capsula</Label>
                <div className="border border-brand-border rounded-sm p-2 max-h-28 overflow-auto space-y-2 bg-white"
                  data-testid={`roles-${member.user_email}`}>
                  {capsuleIds.length === 0 && (
                    <p className="text-[10px] text-zinc-400">Asignale una capsula primero.</p>
                  )}
                  {capsuleIds.map((cid) => {
                    const disponibles = rolesPorCapsula[cid] || [];
                    return (
                      <div key={cid}>
                        <p className="text-[10px] font-semibold text-zinc-500">{cid}</p>
                        {disponibles.length === 0 && (
                          <p className="text-[10px] text-zinc-400">
                            Sin roles definidos. Se crean en Config Capsula → Intents → Roles del equipo.
                          </p>
                        )}
                        {disponibles.map((r) => {
                          const puesto = roles.some((x) => x.capsule_id === cid && x.role_id === r.id);
                          return (
                            <label key={r.id} className="flex items-center gap-2 text-xs" title={r.description || ''}>
                              <input
                                type="checkbox"
                                checked={puesto}
                                onChange={(e) => setRoles((prev) => (
                                  e.target.checked
                                    ? [...prev, { capsule_id: cid, role_id: r.id }]
                                    : prev.filter((x) => !(x.capsule_id === cid && x.role_id === r.id))
                                ))}
                              />
                              <span>{r.name || r.id}</span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Expertise (separado por comas)</Label>
                <Input value={expertise} onChange={e => setExpertise(e.target.value)}
                  className="rounded-sm text-xs h-8" placeholder="Oracle Forms, PL/SQL, Redes" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Estado</Label>
                <select data-testid={`status-${member.user_email}`} value={status}
                  onChange={e => setStatus(e.target.value)}
                  className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8">
                  <option value="active">Activo</option>
                  <option value="vacation">Vacaciones</option>
                  <option value="unavailable">No disponible</option>
                </select>
              </div>
            </div>

            {/* **La sección de contactos se fue a Personas** (2026-08-28). Acá había
                "Contactos de notificación (por prioridad)" con un botón "+ Contacto" que se veía
                igual al "+ Canal" de Personas, y ninguna pantalla explicaba la diferencia.
                Diego lo leyó como lo que era: ruido.

                Eran dos lugares para lo mismo. Ahora el orden y el "avisar por acá" viven en la
                identidad, que es donde ya vivía la dirección. Ver
                `services/por_donde_se_le_avisa.py`. */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-2">
              <p className="text-[11px] text-zinc-600">
                <b>Por dónde se le avisa se configura en Personas</b>, junto con sus canales: ahí
                se ordena cuál se intenta primero y se puede apagar alguno para avisos sin dejar
                de reconocerlo cuando escribe desde ahí.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => onDelete(member.user_email)} className="text-xs text-red-500 h-7"
                  data-testid={`purge-user-${member.user_email}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar usuario
                </Button>
              )}
              <Button size="sm" onClick={handleSave} disabled={saving}
                data-testid={`save-member-${member.user_email}`}
                className="bg-brand-primary text-white rounded-sm text-xs h-7 px-3">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar Equipo
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
    </>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('team');
  const [members, setMembers] = useState([]);
  // Esta pantalla muestra **el equipo**, y nada mas. Antes mostraba a cualquiera que se
  // hubiera logueado alguna vez; despues tuvo un interruptor para ver tambien al resto, que
  // era peor: con `personas` unificada el directorio contiene a todo el que alguna vez
  // escribio, y volcarlo aca iba a ser insostenible apenas fueran cientos. El directorio vive
  // en Personas; para sumar a alguien hay un buscador en "Sumar al equipo".
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
  const [addPersona, setAddPersona] = useState(null);
  const [addQuery, setAddQuery] = useState('');
  const [newUser, setNewUser] = useState({ email: '', full_name: '', username: '', password: '', role: 'operator' });
  const [capsules, setCapsules] = useState([]);
  const [rolesPorCapsula, setRolesPorCapsula] = useState({});
  const [addCapsuleIds, setAddCapsuleIds] = useState([]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);

  // Heartbeat state
  const [hbConfig, setHbConfig] = useState(null);
  const [hbStatus, setHbStatus] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hbCapsuleId, setHbCapsuleId] = useState('');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [identity, setIdentity] = useState(null);
  const [identityMeta, setIdentityMeta] = useState(null);
  const [domainsText, setDomainsText] = useState('');
  const [groupMapText, setGroupMapText] = useState('{}');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [syncingOrg, setSyncingOrg] = useState(false);

  useEffect(() => {
    loadTeam();
    if (user?.role === 'admin' && user?.tenant_id) {
      loadIdentity();
      loadPending();
    }
  }, [user?.role, user?.tenant_id]);

  const loadPending = async () => {
    try {
      const res = await teamAPI.pendingApprovals();
      setPendingUsers(res.data || []);
    } catch (err) { console.error(err); }
  };

  const loadIdentity = async () => {
    try {
      const res = await tenantsAPI.getIdentity(user.tenant_id);
      setIdentity(res.data?.identity || null);
      setIdentityMeta(res.data || null);
      const entra = res.data?.identity?.entra || {};
      setDomainsText((entra.allowed_domains || []).join(', '));
      setGroupMapText(JSON.stringify(entra.group_role_map || {}, null, 2));
    } catch (err) { console.error(err); }
  };

  const saveIdentity = async () => {
    if (!user?.tenant_id) return;
    setSavingIdentity(true);
    try {
      let group_role_map = {};
      try {
        group_role_map = JSON.parse(groupMapText || '{}');
      } catch {
        toast.error('group_role_map debe ser JSON valido');
        setSavingIdentity(false);
        return;
      }
      const domains = domainsText.split(/[,\s]+/).map((d) => d.trim()).filter(Boolean);
      const entra = {
        ...(identity?.entra || {}),
        enabled: !!identity?.entra?.enabled,
        access_policy: identity?.entra?.access_policy || 'require_approval',
        // 'solicitante', igual que el backend (services/tenant_identity.py). Decia 'operator':
        // en un tenant que nunca fijo el rol, abrir esta pantalla y guardar —sin tocar el
        // campo— convertia a todo futuro login por SSO en operator, con acceso a Tickets,
        // Monitor HITL y Personas. La UI no puede tener un default distinto del que aplica el
        // sistema: eso no es un default, es un cambio silencioso.
        default_role: identity?.entra?.default_role || 'solicitante',
        allowed_domains: domains,
        group_role_map,
      };
      const res = await tenantsAPI.putIdentity(user.tenant_id, { entra });
      setIdentity(res.data?.identity || null);
      toast.success('Identidad guardada');
      loadIdentity();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando identidad');
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleSyncOrg = async () => {
    setSyncingOrg(true);
    try {
      const res = await teamAPI.syncOrgProfiles();
      toast.success(`Organigrama: ${res.data?.synced_ok || 0} ok, ${res.data?.synced_error || 0} error`);
      loadTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error sync organigrama (¿skill Graph?)');
    } finally {
      setSyncingOrg(false);
    }
  };
  useEffect(() => {
    if (user?.tenant_id) loadCapsules();
  }, [user?.tenant_id]);

  useEffect(() => {
    if (!capsules?.length) return;
    if (!hbCapsuleId || !capsules.some(c => c.capsule_id === hbCapsuleId)) {
      setHbCapsuleId(capsules[0].capsule_id);
    }
  }, [capsules, hbCapsuleId]);

  useEffect(() => {
    if (hbCapsuleId) loadHeartbeat(hbCapsuleId);
  }, [hbCapsuleId]);

  // La busqueda de personas pega contra el servidor: el directorio puede ser mucho mas
  // grande que lo que tiene sentido traer entero.
  useEffect(() => {
    if (!showAdd) return;
    const t = setTimeout(async () => {
      try {
        const r = await teamAPI.available(addQuery);
        setAvailableUsers(r.data || []);
      } catch { setAvailableUsers([]); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addQuery, showAdd]);

  const loadCapsules = async () => {
    try {
      const res = await capsulesAPI.list(user?.tenant_id);
      const lista = res.data || [];
      setCapsules(lista);
      // El rol es del par (persona, capsula), asi que la pantalla necesita el catalogo de
      // **cada** capsula: la misma persona puede ser Analista Funcional en sistemas y
      // Autorizante en compras.
      const entradas = await Promise.all(lista.map(async (c) => {
        try {
          const cfg = await capsuleConfigAPI.get(user?.tenant_id, c.capsule_id);
          return [c.capsule_id, cfg.data?.team_roles || []];
        } catch { return [c.capsule_id, []]; }
      }));
      setRolesPorCapsula(Object.fromEntries(entradas));
    } catch (err) { console.error(err); }
  };

  const loadTeam = async () => {
    try {
      const [membersRes, availRes] = await Promise.allSettled([teamAPI.list(true), teamAPI.available()]);
      if (membersRes.status === 'fulfilled') setMembers(membersRes.value.data || []);
      if (availRes.status === 'fulfilled') setAvailableUsers(availRes.value.data || []);
    } catch (err) { console.error(err); }
  };

  const loadHeartbeat = async (capsuleId) => {
    const cid = capsuleId || hbCapsuleId;
    if (!cid) return;
    try {
      const [configRes, statusRes, notifRes] = await Promise.allSettled([
        heartbeatAPI.getConfig(cid),
        heartbeatAPI.status(),
        heartbeatAPI.notifications(),
      ]);
      if (configRes.status === 'fulfilled') setHbConfig(configRes.value.data);
      if (statusRes.status === 'fulfilled') setHbStatus(statusRes.value.data);
      if (notifRes.status === 'fulfilled') setNotifications(notifRes.value.data || []);
    } catch (err) { console.error(err); }
  };

  const membersForHbCapsule = (members || []).filter((m) => {
    if (m.status !== 'active') return false;
    const ids = m.capsule_ids || [];
    if (!ids.length) return true; // legacy: todas
    return ids.includes(hbCapsuleId);
  });

  const handleAddMember = async () => {
    if (!addPersona) { toast.error('Elegí una persona'); return; }
    if (!addCapsuleIds.length) { toast.error('Elegí al menos una cápsula'); return; }
    try {
      await teamAPI.create({
        contact_id: addPersona.contact_id,
        user_email: addPersona.email || '',
        capsule_ids: addCapsuleIds,
        expertise: [],
        channels: [],
        notifications_enabled: true,
      });
      toast.success(`${addPersona.display_name} se sumó al equipo`);
      if (!addPersona.tiene_login) {
        // Se dice, no se impide: puede recibir trabajo y avisos por mail sin haber entrado.
        toast.info('Todavía no tiene login: va a recibir avisos por correo, pero no puede entrar a la app hasta que ingrese con Microsoft.');
      }
      setShowAdd(false);
      setAddPersona(null);
      setAddQuery('');
      setAddCapsuleIds([]);
      loadTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error agregando miembro'); }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.username || !newUser.password) {
      toast.error('Completa todos los campos');
      return;
    }
    setCreatingUser(true);
    try {
      await teamAPI.createUser(newUser);
      toast.success(`Usuario ${newUser.full_name} creado exitosamente`);
      setShowCreateUser(false);
      setNewUser({ email: '', full_name: '', username: '', password: '', role: 'operator' });
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando usuario');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateMember = async (email, data) => {
    await teamAPI.update(email, data);
    loadTeam();
  };

  const handleUpdateCredentials = async (email, data) => {
    await teamAPI.updateCredentials(email, data);
    loadTeam();
  };

  const handleDeleteMember = async (email) => {
    setConfirmState({
      open: true,
      title: 'Eliminar usuario',
      description: `Se eliminara la cuenta de login y la ficha de equipo de ${email}. Los tickets no se borran; si estaba asignado, se desasigna.`,
      confirmText: 'Eliminar usuario',
      onConfirm: async () => {
        try {
          await teamAPI.purgeUser(email);
          toast.success('Usuario eliminado');
          loadTeam();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Error eliminando usuario');
        }
      },
    });
  };

  const handleSyncOrgOne = async (email) => {
    try {
      await teamAPI.syncOrgProfileOne(email);
      toast.success('Organigrama actualizado');
      loadTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error sync organigrama');
    }
  };

  const handleSaveHeartbeat = async () => {
    if (!hbConfig || !hbCapsuleId) return;
    setSaving(true);
    try {
      const targets = (hbConfig.notify_targets || [])
        .filter(t => t.user_email && t.channel_type)
        .map(t => ({
          user_email: t.user_email,
          channel_type: t.channel_type,
          address: t.address || '',
        }));
      const payload = {
        enabled: !!hbConfig.enabled,
        timeout_minutes: hbConfig.timeout_minutes || 30,
        max_reminders: hbConfig.max_reminders || 3,
        on_max_action: hbConfig.on_max_action || 'create_ticket',
        notify_targets: targets,
        notify_member: targets[0]?.user_email || null,
        escalation_template: hbConfig.escalation_template || '',
      };
      await heartbeatAPI.updateConfig(hbCapsuleId, payload);
      toast.success(`Heartbeat actualizado (${hbCapsuleId})`);
      loadHeartbeat(hbCapsuleId);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error guardando config'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#09090B]">Equipo y Seguimiento</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Directorio del equipo, notificaciones y heartbeat de conversaciones</p>
        </div>
        <HelpButton section="team" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-brand-surface border border-brand-border rounded-sm mb-4">
          <TabsTrigger value="team" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Users className="w-3 h-3 mr-1" /> Equipo
          </TabsTrigger>
          <TabsTrigger value="heartbeat" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <HeartPulse className="w-3 h-3 mr-1" /> Heartbeat
          </TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Bell className="w-3 h-3 mr-1" /> Notificaciones
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="identity" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
              Identidad
            </TabsTrigger>
          )}
        </TabsList>

        {/* Team Directory */}
        <TabsContent value="team">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-zinc-500">{members.length} miembros del equipo</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={syncingOrg}
                onClick={handleSyncOrg}
                className="text-xs h-7 rounded-sm gap-1"
              >
                {syncingOrg ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Sync organigrama
              </Button>
              {user?.role === 'admin' && (
                <Button variant="outline" size="sm" onClick={() => { setShowCreateUser(!showCreateUser); setShowAdd(false); }}
                  data-testid="create-user-toggle"
                  className="text-xs h-7 rounded-sm gap-1 border-brand-primary text-brand-primary hover:bg-brand-primary/5">
                  <UserPlus className="w-3 h-3" /> Crear Usuario
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { setShowAdd(!showAdd); setShowCreateUser(false); }}
                data-testid="add-team-member-toggle"
                className="text-xs h-7 rounded-sm gap-1">
                <Plus className="w-3 h-3" /> Sumar al equipo
              </Button>
            </div>
          </div>

          {user?.role === 'admin' && pendingUsers.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/40 rounded-sm mb-3">
              <CardHeader className="py-2 px-4">
                <CardTitle className="text-xs font-semibold text-amber-900">Pendientes de aprobacion Microsoft</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-2">
                {pendingUsers.map((p) => (
                  <div key={p.email} className="flex items-center justify-between text-xs bg-white border border-amber-100 rounded-sm px-2 py-1.5">
                    <span>{p.full_name || p.email} · {p.email}</span>
                    <div className="flex gap-1">
                      <Button size="sm" className="h-6 text-[10px] rounded-sm" onClick={async () => {
                        await teamAPI.approveUser(p.email);
                        toast.success('Usuario aprobado');
                        loadPending();
                        loadTeam();
                      }}>Aprobar</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" onClick={async () => {
                        await teamAPI.denyUser(p.email);
                        toast.success('Usuario denegado');
                        loadPending();
                      }}>Denegar</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Create new user form */}
          {showCreateUser && (
            <Card className="border-brand-primary border rounded-sm mb-3" data-testid="create-user-form">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-brand-primary flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" /> Nuevo Usuario
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nombre Completo</Label>
                    <Input data-testid="new-user-fullname" value={newUser.full_name}
                      onChange={e => setNewUser(p => ({ ...p, full_name: e.target.value }))}
                      placeholder="Juan Perez" className="rounded-sm text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Usuario</Label>
                    <Input data-testid="new-user-username" value={newUser.username}
                      onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))}
                      placeholder="jperez" className="rounded-sm text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</Label>
                    <Input data-testid="new-user-email" type="email" value={newUser.email}
                      onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                      placeholder="jperez@acme.com" className="rounded-sm text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Rol</Label>
                    <select data-testid="new-user-role" value={newUser.role}
                      onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}
                      className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8">
                      {/* `solicitante` faltaba: el rol existe desde el 2026-08-18 y no se
                          podia ni poner ni sacar desde acá, así que alguien dado de alta
                          automáticamente quedaba atrapado en el rol más acotado. */}
                      <option value="solicitante">Solicitante (solo ve lo que pidió)</option>
                      <option value="operator">Operador</option>
                      <option value="supervisor">Supervisor</option>
                      {/* Dirección ordena la prioridad de la empresa entre todas las gerencias
                          (2026-10-09). No estaba en este selector, así que no se podía dar. */}
                      <option value="executive">Dirección (ordena la prioridad de la empresa)</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Contrasena</Label>
                  <div className="relative">
                    <Input data-testid="new-user-password" type={showNewPassword ? 'text' : 'password'}
                      value={newUser.password}
                      onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                      placeholder="Minimo 8 caracteres" className="rounded-sm text-xs h-8 pr-9" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                      tabIndex={-1}>
                      {showNewPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowCreateUser(false)} className="text-xs h-7">Cancelar</Button>
                  <Button data-testid="create-user-submit" size="sm" onClick={handleCreateUser} disabled={creatingUser}
                    className="bg-brand-primary text-white rounded-sm text-xs h-7 px-4">
                    {creatingUser ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                    Crear Usuario
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {showAdd && (
            <Card className="border-brand-primary/40 border rounded-sm mb-3">
              <CardContent className="p-3 flex items-center gap-2">
                <div className="flex-1 space-y-2">
                  {/* Un buscador y no un `select` de usuarios: el directorio contiene a todo el
                      que alguna vez escribio, y tener login no es requisito para trabajar aca
                      --a una persona precargada se le puede asignar trabajo y el aviso le llega
                      por mail--. El `select` viejo listaba solo usuarios, asi que en un tenant
                      recien reseteado tenia un solo nombre. */}
                  <Input
                    data-testid="add-member-search"
                    value={addPersona ? addPersona.display_name : addQuery}
                    onChange={(e) => { setAddPersona(null); setAddQuery(e.target.value); }}
                    placeholder="Buscar persona por nombre o correo..."
                    className="h-8 text-xs rounded-sm"
                  />
                  {!addPersona && (
                    <div className="border border-brand-border rounded-sm max-h-40 overflow-auto">
                      {availableUsers.length === 0 && (
                        <p className="p-2 text-[10px] text-zinc-500">
                          {addQuery
                            ? 'Sin coincidencias en el directorio.'
                            : 'Nadie fuera del equipo todavía. Escribí para buscar.'}
                        </p>
                      )}
                      {availableUsers.map((persona) => (
                        <button
                          key={persona.contact_id}
                          type="button"
                          data-testid={`add-member-option-${persona.contact_id}`}
                          onClick={() => setAddPersona(persona)}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
                        >
                          <span className="font-medium">{persona.display_name}</span>
                          {persona.cargo && <span className="text-zinc-400"> · {persona.cargo}</span>}
                          <span className="block text-[10px] text-zinc-500">
                            {persona.email || 'sin correo'}
                            {/* Se muestra, no se prohibe: sin login recibe avisos igual. */}
                            {persona.tiene_login
                              ? <span className="text-emerald-700"> · tiene login</span>
                              : <span className="text-amber-700"> · todavía no ingresó</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="border border-brand-border rounded-sm p-2 max-h-24 overflow-auto grid grid-cols-2 gap-1">
                    {(capsules || []).map((c) => {
                      const checked = addCapsuleIds.includes(c.capsule_id);
                      return (
                        <label key={c.capsule_id} className="flex items-center gap-1 text-[11px]">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setAddCapsuleIds((prev) => [...new Set([...prev, c.capsule_id])]);
                              else setAddCapsuleIds((prev) => {
                                const next = prev.filter((id) => id !== c.capsule_id);
                                return next.length ? next : [];
                              });
                            }}
                          />
                          <span>{c.capsule_id}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <Button data-testid="add-member-button" onClick={handleAddMember}
                  className="bg-brand-primary text-white rounded-sm text-xs h-8 px-3">Agregar</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)} className="h-8 text-xs">Cancelar</Button>
              </CardContent>
            </Card>
          )}

          <p className="text-[10px] text-zinc-400 mb-2">
            Solo quien tiene rol o cápsula. El directorio completo está en <strong>Personas</strong>.
          </p>

          <div className="space-y-2">
            {members.map(m => (
              <TeamMemberCard key={m.user_email} member={m}
                onUpdate={handleUpdateMember} onDelete={handleDeleteMember} onRecargar={loadTeam}
                onUpdateCredentials={handleUpdateCredentials}
                onSyncOrg={user?.role === 'admin' || user?.role === 'supervisor' ? handleSyncOrgOne : undefined}
                isAdmin={user?.role === 'admin'} capsules={capsules}
                rolesPorCapsula={rolesPorCapsula} />
            ))}
            {members.length === 0 && (
              <p className="text-center py-8 text-xs text-zinc-400">
                Nadie tiene todavía un rol o una cápsula asignada. Usá «Sumar al equipo».
              </p>
            )}
          </div>
        </TabsContent>

        {/* ── Heartbeat Config ── */}
        <TabsContent value="heartbeat">
          <div className="space-y-4">
              {/* Capsule selector */}
              <Card className="border rounded-sm">
                <CardContent className="p-4 space-y-2">
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Capsula</Label>
                  <select
                    data-testid="hb-capsule-select"
                    value={hbCapsuleId}
                    onChange={e => setHbCapsuleId(e.target.value)}
                    className="w-full max-w-md border border-brand-border rounded-sm p-2 text-sm h-9"
                  >
                    {!capsules?.length && <option value="">Sin capsulas</option>}
                    {(capsules || []).map(c => (
                      <option key={c.capsule_id} value={c.capsule_id}>{c.capsule_id}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Politica por capsula: Compras y Sistemas se configuran por separado.
                    Desactivar aqui solo saltea conversaciones de esta capsula; el motor del servidor
                    sigue corriendo (PM y tareas de agentes).
                  </p>
                </CardContent>
              </Card>

          {hbConfig && (
            <>
              {/* Status bar */}
              <Card className="border rounded-sm">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${hbStatus?.engine_running ? 'bg-green-500 animate-pulse' : 'bg-zinc-300'}`} />
                    <div>
                      <span className="text-sm font-medium">{hbStatus?.engine_running ? 'Motor activo' : 'Motor detenido'}</span>
                      <span className="text-[10px] text-zinc-400 ml-3">Intervalo: cada {hbStatus?.interval_seconds || 60}s</span>
                      {hbConfig._policy_source && (
                        <span className="text-[10px] text-zinc-400 ml-3">
                          Fuente: {hbConfig._policy_source === 'legacy_tenant' ? 'legacy tenant (sin guardar aun)' : hbConfig._policy_source}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span data-testid="hb-monitored">{hbStatus?.conversations_monitored || 0} conv. monitoreadas</span>
                    <span>{hbStatus?.conversations_with_reminders || 0} con recordatorios</span>
                  </div>
                </CardContent>
              </Card>

              {/* Config */}
              <Card className="border rounded-sm">
                <CardHeader className="pb-3 border-b border-brand-border">
                  <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2">
                    <HeartPulse className="w-3.5 h-3.5 text-brand-primary" /> Configuracion del Heartbeat
                    {hbCapsuleId && <span className="normal-case font-mono text-zinc-400">({hbCapsuleId})</span>}
                  </CardTitle>
                  <p className="text-[10px] text-zinc-500 font-normal normal-case mt-1 leading-relaxed">
                    Esta pantalla es la <strong>politica</strong> (cuándo y cuántos ticks; accion al agotar).
                    Cada tick lo ejecuta el <strong>agente de la conversacion</strong> con SOUL/RULES y, si esta ON,
                    el playbook de la skill <code className="bg-zinc-100 px-0.5">heartbeat-followup</code>.
                    Si el agente responde <code className="bg-zinc-100 px-0.5">HEARTBEAT_OK</code>, no se envia mensaje al usuario
                    (silencio util). Desactivar la skill en Agentes no apaga este motor.
                  </p>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium">Heartbeat habilitado</span>
                      <p className="text-[10px] text-zinc-400">Solo para conversaciones de esta capsula</p>
                    </div>
                    <Switch data-testid="hb-enabled-toggle" checked={hbConfig.enabled}
                      onCheckedChange={v => setHbConfig(p => ({ ...p, enabled: v }))} />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Timeout (minutos)</Label>
                      <Input data-testid="hb-timeout" type="number" min="1" value={hbConfig.timeout_minutes}
                        onChange={e => setHbConfig(p => ({ ...p, timeout_minutes: parseInt(e.target.value) || 30 }))}
                        className="rounded-sm text-sm h-9" />
                      <p className="text-[9px] text-zinc-400 mt-0.5">Tiempo de espera antes del primer recordatorio</p>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Max Recordatorios</Label>
                      <Input data-testid="hb-max-reminders" type="number" min="1" max="10" value={hbConfig.max_reminders}
                        onChange={e => setHbConfig(p => ({ ...p, max_reminders: parseInt(e.target.value) || 3 }))}
                        className="rounded-sm text-sm h-9" />
                      <p className="text-[9px] text-zinc-400 mt-0.5">Recordatorios antes de actuar</p>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Accion al agotar</Label>
                      <select data-testid="hb-on-max" value={hbConfig.on_max_action}
                        onChange={e => setHbConfig(p => ({ ...p, on_max_action: e.target.value }))}
                        className="w-full border border-brand-border rounded-sm p-2 text-sm h-9">
                        <option value="create_ticket">Crear ticket</option>
                        <option value="notify_team">Notificar al equipo</option>
                        <option value="both">Ambos (ticket + notificar)</option>
                      </select>
                      <p className="text-[9px] text-zinc-400 mt-0.5">Que hacer al superar max recordatorios</p>
                    </div>
                  </div>

                  {(hbConfig.on_max_action === 'notify_team' || hbConfig.on_max_action === 'both') && (
                    <div className="space-y-2 border border-brand-border rounded-sm p-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Destinatarios al agotar</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid="hb-add-target"
                          className="h-7 text-xs"
                          onClick={() => {
                            const first = membersForHbCapsule[0];
                            const ch = (first?.channels || []).find(c => c.enabled !== false) || {};
                            setHbConfig(p => ({
                              ...p,
                              notify_targets: [
                                ...(p.notify_targets || []),
                                {
                                  user_email: first?.user_email || '',
                                  channel_type: ch.channel_type || 'email',
                                  address: ch.address || '',
                                },
                              ],
                            }));
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Agregar
                        </Button>
                      </div>
                      <p className="text-[9px] text-zinc-400 leading-relaxed">
                        Destino de aviso (agenda del miembro). El emisor lo aporta el canal de la capsula.
                        Se listan miembros de esta capsula; sin <code className="bg-zinc-100 px-0.5">capsule_ids</code> = todas.
                      </p>
                      {(hbConfig.notify_targets || []).length === 0 && (
                        <p className="text-[10px] text-zinc-400">Sin destinatarios. Se usara el primer miembro activo disponible.</p>
                      )}
                      {(hbConfig.notify_targets || []).map((t, idx) => {
                        const member = membersForHbCapsule.find(m => m.user_email === t.user_email)
                          || members.find(m => m.user_email === t.user_email);
                        const memberChannels = (member?.channels || []).filter(c => c.enabled !== false);
                        return (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`hb-target-${idx}`}>
                            <div className="col-span-4">
                              <Label className="text-[9px] text-zinc-500">Miembro</Label>
                              <select
                                value={t.user_email || ''}
                                onChange={e => {
                                  const email = e.target.value;
                                  const m = membersForHbCapsule.find(x => x.user_email === email)
                                    || members.find(x => x.user_email === email);
                                  const ch = (m?.channels || []).find(c => c.enabled !== false) || {};
                                  setHbConfig(p => ({
                                    ...p,
                                    notify_targets: (p.notify_targets || []).map((row, i) =>
                                      i === idx
                                        ? { user_email: email, channel_type: ch.channel_type || 'email', address: ch.address || '' }
                                        : row
                                    ),
                                  }));
                                }}
                                className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8"
                              >
                                <option value="">Seleccionar...</option>
                                {membersForHbCapsule.map(m => (
                                  <option key={m.user_email} value={m.user_email}>{m.full_name || m.user_email}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-span-3">
                              <Label className="text-[9px] text-zinc-500">Canal</Label>
                              <select
                                value={t.channel_type || ''}
                                onChange={e => {
                                  const ctype = e.target.value;
                                  const ch = memberChannels.find(c => c.channel_type === ctype) || {};
                                  setHbConfig(p => ({
                                    ...p,
                                    notify_targets: (p.notify_targets || []).map((row, i) =>
                                      i === idx
                                        ? { ...row, channel_type: ctype, address: ch.address || '' }
                                        : row
                                    ),
                                  }));
                                }}
                                className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8"
                              >
                                {memberChannels.length === 0 && <option value="">Sin canales</option>}
                                {memberChannels.map(c => (
                                  <option key={`${c.channel_type}-${c.address}`} value={c.channel_type}>
                                    {CHANNEL_TYPES.find(x => x.value === c.channel_type)?.label || c.channel_type}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-span-4">
                              <Label className="text-[9px] text-zinc-500">Contacto</Label>
                              <Input
                                readOnly
                                value={t.address || ''}
                                className="rounded-sm text-xs h-8 bg-zinc-50"
                                placeholder="Se completa desde el canal del miembro"
                              />
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500"
                                onClick={() => setHbConfig(p => ({
                                  ...p,
                                  notify_targets: (p.notify_targets || []).filter((_, i) => i !== idx),
                                }))}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Mensaje cuando el usuario no responde</Label>
                    <textarea
                      data-testid="hb-escalation-template"
                      value={hbConfig.escalation_template || ''}
                      onChange={e => setHbConfig(p => ({ ...p, escalation_template: e.target.value }))}
                      rows={3}
                      className="w-full border border-brand-border rounded-sm p-2 text-sm font-mono"
                      placeholder="El usuario no respondio despues de {recordatorios} recordatorios. Acciones: {acciones}."
                    />
                    <p className="text-[9px] text-zinc-400 mt-0.5">
                      Variables: {'{recordatorios}'}, {'{acciones}'}, {'{ticket_id}'}, {'{agente}'}, {'{conversacion}'}, {'{canal}'}
                    </p>
                  </div>

                  <Button data-testid="save-heartbeat-config" onClick={handleSaveHeartbeat} disabled={saving || !hbCapsuleId}
                    className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-sm">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Guardar Heartbeat
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
          </div>
        </TabsContent>

        {/* ── Notifications ── */}
        <TabsContent value="notifications">
          <div className="space-y-2">
            {notifications.length === 0 ? (
              <p className="text-center py-8 text-xs text-zinc-400">No hay notificaciones recientes.</p>
            ) : (
              notifications.map((n, i) => (
                <Card key={n.notification_id || i} className="border rounded-sm">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell className={`w-4 h-4 ${n.status === 'sent' ? 'text-green-500' : 'text-amber-400'}`} />
                      <div>
                        <span className="text-xs font-medium">{n.message?.slice(0, 100)}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-zinc-400">Para: {n.recipient_email}</span>
                          <Badge variant={n.status === 'sent' ? 'default' : 'secondary'} className="text-[9px] rounded-sm">{n.status}</Badge>
                          {n.channels_attempted?.map((ch, j) => (
                            <span key={j} className="text-[9px] text-zinc-400 flex items-center gap-0.5">
                              {ch.sent ? <Wifi className="w-2.5 h-2.5 text-green-500" /> : <WifiOff className="w-2.5 h-2.5 text-red-400" />}
                              {ch.channel}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] text-zinc-400">{n.created_at?.slice(0, 16)}</span>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="identity">
            <Card className="border rounded-sm">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold">Login Microsoft (Entra)</CardTitle>
                <p className="text-[11px] text-zinc-500 mt-1">
                  App multi-tenant MINKA via env del servidor. Aqui solo politicas del tenant.
                  {identityMeta?.platform_configured === false && (
                    <span className="text-amber-700"> Falta OMNISYS_ENTRA_* en el backend.</span>
                  )}
                </p>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Habilitar Entrar con Microsoft</Label>
                  <Switch
                    checked={!!identity?.entra?.enabled}
                    onCheckedChange={(v) => setIdentity((p) => ({
                      ...(p || {}),
                      entra: { ...(p?.entra || {}), enabled: v },
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">Dominios permitidos</Label>
                  <Input
                    value={domainsText}
                    onChange={(e) => setDomainsText(e.target.value)}
                    placeholder="pritty.com.ar"
                    className="rounded-sm text-xs h-8 mt-1"
                  />
                </div>
                {/* Solo lectura (2026-10-15): el directorio lo fija el primer login y desde ahi
                    una cuenta de otra organizacion de Microsoft no entra, aunque su dominio
                    coincida. Se muestra para que se sepa que filtro esta aplicando. */}
                <div data-testid="identity-directorio">
                  <Label className="text-[10px] uppercase text-zinc-500">Directorio de Microsoft habilitado</Label>
                  <p className="text-xs font-mono mt-1 text-zinc-700">
                    {identity?.entra?.directory_tenant_id || 'Todavía ninguno: lo fija el primer ingreso con Microsoft.'}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Solo entran cuentas de este directorio y de los dominios permitidos.
                  </p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">Politica de acceso</Label>
                  <select
                    className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8 mt-1"
                    value={identity?.entra?.access_policy || 'require_approval'}
                    onChange={(e) => setIdentity((p) => ({
                      ...(p || {}),
                      entra: { ...(p?.entra || {}), access_policy: e.target.value },
                    }))}
                  >
                    <option value="require_approval">Requiere aprobacion</option>
                    <option value="allow">Permitir (alta automatica con el rol por defecto)</option>
                    <option value="deny">Denegar</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">Rol por defecto</Label>
                  <select
                    className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8 mt-1"
                    value={identity?.entra?.default_role || 'solicitante'}
                    onChange={(e) => setIdentity((p) => ({
                      ...(p || {}),
                      entra: { ...(p?.entra || {}), default_role: e.target.value },
                    }))}
                  >
                    <option value="solicitante">solicitante (solo lo suyo)</option>
                    <option value="operator">operator</option>
                    <option value="supervisor">supervisor</option>
                    <option value="executive">executive</option>
                    <option value="viewer">viewer</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">group_role_map (JSON object-id → rol)</Label>
                  <textarea
                    value={groupMapText}
                    onChange={(e) => setGroupMapText(e.target.value)}
                    className="w-full border border-brand-border rounded-sm p-2 text-xs font-mono min-h-[80px] mt-1"
                  />
                </div>
                {identityMeta?.admin_consent_url && (
                  <a
                    href={identityMeta.admin_consent_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-brand-primary underline"
                  >
                    Abrir admin consent (opcional)
                  </a>
                )}
                <Button
                  size="sm"
                  className="rounded-sm text-xs"
                  disabled={savingIdentity}
                  onClick={saveIdentity}
                >
                  {savingIdentity ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  Guardar identidad
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
    </div>
  );
}
